/**
 * WHAT WE SEND YOU, at `/account/sending` (ops's section 11.3, the draft ops builds with its phase 2;
 * home's slices H5 and H7, `docs/seam.md` P114). Her choices of what we send her, as ops last pushed
 * them, and her switches, each sent up as `preferences.set`, and her texts turned off as
 * `texts.stop`. Account offers it only once ops has pushed her choices, and the Worker answers 404
 * until then, which sends this page back to Account, so nothing new shows before ops sends them.
 *
 * No artboard in `faithfully-kept-business/design/home/` draws it yet (business issue 13's
 * `Settings.dc.html`, the proposal's B2): it is built from the page home's proposal sketches
 * (`docs/proposal-messages-and-documents.md`, section 4.2), in today's look, in the current tokens
 * and parts, as Account's other pages are. The words are `SENDING` in `words.ts`, NEW.
 *
 * - "BACK TO ACCOUNT", then "Account" over "What we send you", and what the page does; her pause
 *   while it has not ended, how her messages come, each or once a day at ops's `summary_time`, and
 *   her email's state where no email reaches her (no address, a mark, or no yes to email) or emails
 *   are not arriving, with the office's number.
 * - HER KINDS OF MESSAGE in 11.3's order, in a card, and a card per home where ops sent choices for
 *   one home: each with what it holds, or for reminders their days and time of day; the email switch
 *   (`role="switch"`, named in full, 48 tall), held on by its lock with the lock's reason, or no
 *   switch where ops offers no email for it; "Also by text while your texts are on."; 11.3's guard,
 *   "With email and texts off for this, the office will call you."; the office's hold and who last
 *   changed it, read-only. For reminders, the days before and the time of day, sent with the
 *   switch's state as one row.
 * - A SWITCH PRESSED goes up, and stays as ops last said, dimmed and not pressable, with what is
 *   happening under it and in the page's status region, until ops's push says otherwise (CLAUDE.md
 *   binding rule 1). Once the door has taken it, the page holds it so itself, its "Turning ..." line
 *   shown, until a read made after the door answered shows the ask or its answer, so a tap always
 *   shows and is never sent twice; a read that did not go through says so, with the office's number,
 *   and holds the switch still. What she asked is kept for each change that waits, and once ops's
 *   push answers it, "Saved." is said only where the setting is now what she asked (for her texts,
 *   only once they are no longer on); otherwise that the office kept it as it was, with the number.
 *   Ops's refusal, a refusal at the door, a limit, a dropped connection and a send home gave up are
 *   each said under it, with the office's number, never silent. While anything waits the page reads
 *   itself again, gently (`lib/readAgain.ts`), and says so once it has stopped.
 * - THE ALWAYS-ON EMAILS, in a line of their own, no switch.
 * - TEXTS, where ops says how they stand: on, with "Turn off texts", which asks first and says how
 *   they come back; off, by her choice, by STOP or by a mark, each with its one way back, as ops's
 *   `turn_on_by` names it (ops #76 of 2026-10-23): the website's box, her START to the business line
 *   (`keyword`, always for a STOP), or a call; no consent or no number, each with how to get them.
 *   Never a way to turn them on from home: 11.3's `texts.start` is phase 3's.
 * - A KIND HER STOP CHANGED says so, with its day (`changed` `{ by: "self", how: "stop" }`).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  LEAD_DAYS,
  SEND_TIMES,
  getSending,
  sendPreference,
  stopTexts,
  type AskAnswer,
  type PlaceAnswer,
  type SendingAsk,
  type SendingPlace,
  type SendingSection,
  type Setting,
} from '../api.ts';
import { CARD_BUTTON, GHOST_BUTTON, NAVY_BUTTON } from '../components/buttons.ts';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone, WithPhone } from '../components/Phone.tsx';
import { Shell } from '../components/Shell.tsx';
import { PHONE_SHOWN } from '../contact.ts';
import { askingFor, keyFor, type Asking } from '../lib/asks.ts';
import { useReadAgain } from '../lib/readAgain.ts';
import { ACCOUNT_PATH } from '../paths.ts';
import {
  FAILED_BEFORE_PHONE,
  HOLDING,
  PAYING,
  REQUEST,
  SENDING,
  TEXT_START,
  TEXTS_OFF_BY_STOP,
  changedLine,
  emailFor,
  heldLine,
  pausedLine,
  remindersWhen,
  summaryLine,
  textHoursLine,
  tooManyBeforePhone,
  turningEmail,
} from '../words.ts';
import { AccountPage } from './Account.tsx';

/** The longest wait between two reads while a change waits on ops, and how long it reads for. */
export const SENDING_READ_CAP_MS = 60_000;
export const SENDING_READ_BOUND_MS = 15 * 60_000;

const CARD = 'rounded-card border border-border-subtle bg-surface-raised px-5';
const SECTION_HEADING = 'font-serif text-h3 font-semibold text-text-body';
const LINE = 'mt-1 text-small text-pretty text-text-body';
const MUTED = 'mt-1 text-small text-pretty text-text-muted';

/** The page as one read gave it, numbered by that read, the first read 1. */
type ReadPlace = SendingPlace & { readonly read: number };

export function AccountSending(signed: Signed) {
  /** How many reads of the page have been asked for. */
  const reads = useRef(0);
  /**
   * The number of the newest read asked for where it did not go through, so the page drawn is an
   * earlier read's, or 0 where the newest read went through.
   */
  const [failedRead, setFailedRead] = useState(0);
  const load = useCallback(async (): Promise<PlaceAnswer<ReadPlace>> => {
    reads.current += 1;
    const read = reads.current;
    const got = await getSending();
    if (read === reads.current) setFailedRead(got.outcome === 'failed' ? read : 0);
    return got.outcome === 'shown' ? { outcome: 'shown', place: { ...got.place, read } } : got;
  }, []);
  const readsAsked = useCallback(() => reads.current, []);
  return (
    <Guarded
      load={load}
      signed={signed}
      at={null}
      heading={SENDING.heading}
      notFound={<Navigate to={ACCOUNT_PATH} replace />}
    >
      {(place, reload) => (
        <SendingDrawn
          place={place}
          signed={signed}
          reload={reload}
          failedRead={failedRead}
          readsAsked={readsAsked}
        />
      )}
    </Guarded>
  );
}

/** The key a setting is held and said by: its home, or every home, and its kind. */
const keyOf = (setting: Setting): string => `${setting.homeId ?? ''}:${setting.category}`;

/** An ask still on its way: sent from here, not yet answered by ops's push. */
const waiting = (ask: SendingAsk | undefined): boolean =>
  ask?.state === 'sending' || ask?.state === 'waiting' || ask?.state === 'sent';

/** What the door answered a press that wrote nothing, said under what was pressed. */
type Problem = Exclude<AskAnswer, { readonly outcome: 'sent' }>;

/** Her texts asked off, the one thing her texts' press asks. */
const TEXTS_OFF = 'texts_off';

/** What one change asked: a kind's email and, for reminders, their days and time; or texts off. */
type Asked = Choice | typeof TEXTS_OFF;

/** A press the door took from this page, held so until a read made after it shows its ask. */
interface SentHere {
  readonly asked: Asked;
  /** How many reads had been asked for when the door answered: a later read shows it. */
  readonly after: number;
}

/** What one change came to once ops's push answered it: saved as she asked, or kept as it was. */
type Ended = 'saved' | 'kept';

interface DrawnProps {
  readonly place: ReadPlace;
  readonly signed: Signed;
  readonly reload: () => void;
  /** The number of the newest read where it did not go through, or 0 where it went through. */
  readonly failedRead: number;
  /** How many reads have been asked for so far. */
  readonly readsAsked: () => number;
}

/** What the ask on her page asked for, or null where it names no choice. */
function askedIn(ask: SendingAsk): Choice | null {
  if (ask.choice === undefined) return null;
  return {
    email: ask.choice,
    ...(ask.leadDays === undefined ? {} : { leadDays: ask.leadDays }),
    ...(ask.sendTime === undefined ? {} : { sendTime: ask.sendTime }),
  };
}

/**
 * What a change that waited came to, once the page shows no ask of it on its way: saved where the
 * setting is now what she asked, for reminders their days and time too, and for her texts once they
 * are no longer on; kept where it is as it was. Null where the page says something else of it: a
 * refusal or a send given up, said under it, or a kind no longer on her page.
 */
function endOf(place: SendingPlace, key: string, asked: Asked): Ended | null {
  if (key === TEXTS_KEY) {
    if (place.textsAsk !== undefined) return null;
    return place.texts?.state === 'on' ? 'kept' : 'saved';
  }
  const setting = place.sections
    .flatMap((section) => section.settings)
    .find((each) => keyOf(each) === key);
  if (setting === undefined || setting.ask !== undefined || asked === TEXTS_OFF) return null;
  const same =
    setting.email === asked.email &&
    (setting.category !== 'visit_reminders' ||
      (setting.leadDays === asked.leadDays && setting.sendTime === asked.sendTime));
  return same ? 'saved' : 'kept';
}

function SendingDrawn({ place, signed, reload, failedRead, readsAsked }: DrawnProps) {
  /** What the page says in its status region: what a press is doing, "Saved." or kept. */
  const [said, setSaid] = useState('');
  /** A press the door did not take, by the key of what was pressed. */
  const [problems, setProblems] = useState<ReadonlyMap<string, Problem>>(new Map());
  /** Each press's being out and its keys, held by the page for as long as it is shown. */
  const asking = useRef(new Map<string, Asking>());
  /** Each press the door took, by its key, until a read made after it shows its ask or answer. */
  const [sentHere, setSentHere] = useState<ReadonlyMap<string, SentHere>>(new Map());
  /** The keys whose change ops's push answered with the setting as it was, each said under it. */
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set());

  /** The presses held here whose read, asked for after the door took them, did not go through. */
  const unread: ReadonlySet<string> = new Set(
    [...sentHere].filter(([, sent]) => failedRead > sent.after).map(([key]) => key),
  );

  const settings = place.sections.flatMap((section) => section.settings);
  const anyWaiting =
    settings.some((setting) => waiting(setting.ask)) ||
    waiting(place.textsAsk) ||
    sentHere.size > 0;
  const { stopped, again } = useReadAgain(reload, {
    on: anyWaiting,
    capMs: SENDING_READ_CAP_MS,
    boundMs: SENDING_READ_BOUND_MS,
  });

  // What she asked, for each key whose change waited on ops on the last read, so its end is said:
  // "Saved." where the setting is now what she asked, and kept where it is as it was.
  const waitedBefore = useRef<ReadonlyMap<string, Asked>>(new Map());
  useEffect(() => {
    // A press the door took stays held here until a read asked for after the door answered.
    const stillHere = new Map([...sentHere].filter(([, sent]) => place.read <= sent.after));
    const before = new Map(waitedBefore.current);
    for (const [key, sent] of sentHere) if (!stillHere.has(key)) before.set(key, sent.asked);
    const now = new Map<string, Asked>();
    for (const setting of settings) {
      if (!waiting(setting.ask)) continue;
      const asked = askedIn(setting.ask!) ?? before.get(keyOf(setting));
      if (asked !== undefined) now.set(keyOf(setting), asked);
    }
    if (waiting(place.textsAsk)) now.set(TEXTS_KEY, TEXTS_OFF);
    const ended = new Map<string, Ended>();
    for (const [key, asked] of before) {
      if (now.has(key) || stillHere.has(key)) continue;
      const end = endOf(place, key, asked);
      if (end !== null) ended.set(key, end);
    }
    waitedBefore.current = now;
    if (stillHere.size !== sentHere.size) setSentHere(stillHere);
    const keptNow = [...ended].filter(([, end]) => end === 'kept').map(([key]) => key);
    if (keptNow.length > 0) {
      setKept((before) => new Set([...before, ...keptNow]));
      const words = keptNow.every((key) => key === TEXTS_KEY)
        ? SENDING.textsKeptBeforePhone
        : SENDING.keptBeforePhone;
      setSaid(`${words} ${PHONE_SHOWN}.`);
    } else if (ended.size > 0) {
      setSaid(SENDING.saved);
    }
    // Read once per place the Worker answers.
  }, [place]);

  // A read after a press the door took did not go through: said, and the switch held still.
  useEffect(() => {
    if (unread.size > 0) setSaid(`${SENDING.unreadBeforePhone} ${PHONE_SHOWN}.`);
  }, [failedRead, sentHere]);

  /** Sends one press through the door, under the key of what it asks, and says what came of it. */
  async function press(
    key: string,
    asked: Asked,
    answer: string,
    send: (requestKey: string) => Promise<AskAnswer>,
    saying: string,
  ) {
    const held = askingFor(asking.current, key);
    if (held.out || sentHere.has(key)) return;
    held.out = true;
    setProblems((before) => without(before, key));
    setKept((before) => withoutKey(before, key));
    setSaid(saying);
    const answered = await send(keyFor(held, answer));
    held.out = false;
    if (answered.outcome === 'sent') {
      held.keys.clear();
      // Held here until a read asked for from now on shows its ask or its answer.
      const after = readsAsked();
      setSentHere((before) => new Map(before).set(key, { asked, after }));
      reload();
      return;
    }
    if (answered.outcome === 'signed_out') {
      signed.onSignedOut();
      return;
    }
    setSaid('');
    if (answered.outcome === 'not_found') {
      reload();
      return;
    }
    setProblems((before) => new Map(before).set(key, answered));
  }

  const choose = (setting: Setting, choice: Choice) => {
    const title = SENDING.titles[setting.category];
    const onlyWhen = choice.email === setting.email;
    void press(
      keyOf(setting),
      choice,
      `${choice.email}:${choice.leadDays ?? ''}:${choice.sendTime ?? ''}`,
      (requestKey) =>
        sendPreference(requestKey, place.household.id, {
          category: setting.category,
          ...(setting.homeId === undefined ? {} : { homeId: setting.homeId }),
          email: choice.email,
          ...(setting.category === 'visit_reminders'
            ? { leadDays: choice.leadDays, sendTime: choice.sendTime }
            : {}),
        }),
      onlyWhen ? SENDING.changingReminders : turningEmail(choice.email, title),
    );
  };

  const several = place.sections.some((section) => section.homeId !== undefined);
  return (
    <Shell at={null} payments={place.household.payments}>
      <AccountPage heading={SENDING.heading}>
        <p className="mt-2 text-body text-pretty">{SENDING.intro}</p>
        {place.summary ? (
          <p className={LINE}>{summaryLine(place.summary.every, place.summary.time)}</p>
        ) : null}
        {place.paused ? <p className={LINE}>{pausedLine(place.paused)}</p> : null}
        <EmailState state={place.email?.state} />
        {stopped && anyWaiting ? (
          <div className="mt-2">
            <p className={MUTED}>{HOLDING.stopped}</p>
            <button type="button" onClick={again} className={`mt-2 cursor-pointer ${CARD_BUTTON}`}>
              {HOLDING.checkAgain}
            </button>
          </div>
        ) : null}
      </AccountPage>

      {place.sections.map((section) => (
        <SectionCard
          key={section.homeId ?? ''}
          section={section}
          titled={several}
          problems={problems}
          choose={choose}
          texts={place.texts?.state}
          sentHere={sentHere}
          kept={kept}
          unread={unread}
        />
      ))}

      <p data-always className="text-small text-pretty text-text-body">
        {place.role === 'client' ? SENDING.alwaysClient : SENDING.alwaysMember}
      </p>

      {place.texts && TEXTS_STATES.has(place.texts.state) ? (
        <Texts
          place={place}
          problem={problems.get(TEXTS_KEY)}
          here={sentHere.has(TEXTS_KEY)}
          keptAsTheyWere={kept.has(TEXTS_KEY)}
          unread={unread.has(TEXTS_KEY)}
          stop={() =>
            void press(
              TEXTS_KEY,
              TEXTS_OFF,
              'stop',
              (requestKey) => stopTexts(requestKey, place.household.id),
              SENDING.turningOffTexts,
            )
          }
        />
      ) : null}

      <p role="status" className="sr-only">
        {said}
      </p>
    </Shell>
  );
}

/** The key her texts' press is held and said by. */
const TEXTS_KEY = 'texts';

/** Her texts' states as 11.3 names them: a word ops adds later draws no Texts at all. */
const TEXTS_STATES: ReadonlySet<string> = new Set([
  'on',
  'off_by_choice',
  'off_by_stop',
  'marked',
  'no_consent',
  'none',
]);

/** A map without one key, the same map where it held none. */
function without<K, V>(map: ReadonlyMap<K, V>, key: K): ReadonlyMap<K, V> {
  if (!map.has(key)) return map;
  const next = new Map(map);
  next.delete(key);
  return next;
}

/** A set without one key, the same set where it held none. */
function withoutKey<K>(set: ReadonlySet<K>, key: K): ReadonlySet<K> {
  if (!set.has(key)) return set;
  const next = new Set(set);
  next.delete(key);
  return next;
}

/** What one press asks: email on or off, and for reminders their days and time of day. */
interface Choice {
  readonly email: 'on' | 'off';
  readonly leadDays?: number;
  readonly sendTime?: string;
}

/**
 * Her email's state, where no email reaches her or emails are not arriving, with the number: no
 * address, a mark, no yes to email (`no_consent`, ops #76 of 2026-10-23), or `bouncing`, which ops
 * sends in no build yet. A word home does not know draws nothing.
 */
const EMAIL_STATES: Readonly<Record<string, string>> = {
  none: SENDING.noEmailBeforePhone,
  marked: SENDING.emailMarkedBeforePhone,
  no_consent: SENDING.emailNoConsentBeforePhone,
  bouncing: SENDING.emailBouncingBeforePhone,
};

function EmailState({ state }: { readonly state: string | undefined }) {
  const words =
    state === undefined || !Object.hasOwn(EMAIL_STATES, state) ? null : EMAIL_STATES[state]!;
  if (words === null) return null;
  return (
    <p data-email-state className="mt-3 text-body text-pretty">
      {words} <Phone after="." />
    </p>
  );
}

function SectionCard({
  section,
  titled,
  problems,
  choose,
  texts,
  sentHere,
  kept,
  unread,
}: {
  readonly section: SendingSection;
  readonly titled: boolean;
  readonly problems: ReadonlyMap<string, Problem>;
  readonly choose: (setting: Setting, choice: Choice) => void;
  readonly texts: string | undefined;
  readonly sentHere: ReadonlyMap<string, SentHere>;
  readonly kept: ReadonlySet<string>;
  readonly unread: ReadonlySet<string>;
}) {
  const headingId = useId();
  const heading = section.homeId === undefined ? SENDING.everyHome : (section.label ?? '');
  return (
    <section
      data-sending-section={section.homeId ?? ''}
      className={CARD}
      {...(titled ? { 'aria-labelledby': headingId } : {})}
    >
      {titled ? (
        <h2 id={headingId} className={`pt-5 ${SECTION_HEADING}`}>
          {heading}
        </h2>
      ) : null}
      <div className="divide-y divide-border-subtle">
        {section.settings.map((setting) => (
          <SettingRow
            key={keyOf(setting)}
            setting={setting}
            problem={problems.get(keyOf(setting))}
            choose={choose}
            texts={texts}
            here={sentHere.get(keyOf(setting))?.asked}
            kept={kept.has(keyOf(setting))}
            unread={unread.has(keyOf(setting))}
          />
        ))}
      </div>
    </section>
  );
}

/** The line under a kind held on by its lock: the lock's reason, as 11.3 names the lock. */
function lockLine(setting: Setting): { readonly words: string; readonly phone: boolean } | null {
  if (setting.locked === undefined) return null;
  if (setting.locked === 'one_way') return { words: SENDING.oneWay, phone: false };
  if (setting.switch !== 'locked') return null;
  if (setting.locked === 'email' && setting.category === 'invoices') {
    return { words: SENDING.invoicesLocked, phone: false };
  }
  if (setting.locked === 'email' && setting.category === 'visit_changes') {
    return { words: SENDING.changesLocked, phone: false };
  }
  return { words: SENDING.lockedBeforePhone, phone: true };
}

/** What a refusal says, ops's or the door's, by its code: each with the number but STOP's. */
function refusedWords(code: string | undefined): {
  readonly words: string;
  readonly phone: boolean;
} {
  switch (code) {
    case 'locked':
      return { words: SENDING.refusedLockedBeforePhone, phone: true };
    case 'not_offered':
      return { words: SENDING.refusedNotOfferedBeforePhone, phone: true };
    case 'marked':
      return { words: SENDING.emailMarkedBeforePhone, phone: true };
    case 'texts_stopped':
      return { words: TEXTS_OFF_BY_STOP, phone: false };
    default:
      return { words: SENDING.refusedBeforePhone, phone: true };
  }
}

/** A line that may end in the office's number. */
function Said({ words, phone }: { readonly words: string; readonly phone: boolean }) {
  return (
    <p className={LINE}>
      {phone ? (
        <>
          {words} <Phone after="." />
        </>
      ) : (
        <WithPhone words={words} />
      )}
    </p>
  );
}

/** Where her ask stands, under what she pressed, while it is said. */
function AskSaid({ ask, sending }: { readonly ask: SendingAsk; readonly sending: string }) {
  switch (ask.state) {
    case 'sending':
      return <p className={MUTED}>{sending}</p>;
    case 'waiting':
      return <p className={MUTED}>{SENDING.waiting}</p>;
    case 'sent':
      return <p className={MUTED}>{REQUEST.sent}</p>;
    case 'refused':
      return <Said {...refusedWords(ask.code)} />;
    case 'stopped':
      return <Said words={SENDING.refusedBeforePhone} phone />;
  }
}

/** What the door said of a press it did not take, under what was pressed. */
function ProblemSaid({ problem }: { readonly problem: Problem }) {
  switch (problem.outcome) {
    case 'too_many':
      return <Said words={tooManyBeforePhone(problem.limit)} phone />;
    case 'refused':
      return <Said {...refusedWords(problem.code)} />;
    case 'failed':
      return (
        <Said
          words={problem.offline ? PAYING.connectionDroppedBeforePhone : FAILED_BEFORE_PHONE}
          phone
        />
      );
    default:
      return <Said words={SENDING.refusedBeforePhone} phone />;
  }
}

function SettingRow({
  setting,
  problem,
  choose,
  texts,
  here,
  kept,
  unread,
}: {
  readonly setting: Setting;
  readonly problem: Problem | undefined;
  readonly choose: (setting: Setting, choice: Choice) => void;
  readonly texts: string | undefined;
  /** What a press the door took from this page asked, while it is held here. */
  readonly here: Asked | undefined;
  /** Ops's push answered her change with this kind as it was. */
  readonly kept: boolean;
  readonly unread: boolean;
}) {
  const title = SENDING.titles[setting.category];
  const aboutId = useId();
  const reminders = setting.category === 'visit_reminders';
  const about = reminders
    ? remindersWhen(setting.leadDays, setting.sendTime)
    : SENDING.about[setting.category];
  // A press the door took is said as on its way until a read made after it shows its ask.
  const ask: SendingAsk | undefined =
    here !== undefined && here !== TEXTS_OFF
      ? { state: 'sending', choice: here.email }
      : setting.ask;
  const pending = waiting(ask);
  const lock = lockLine(setting);
  const held = setting.held ? heldLine(setting.held.from, setting.held.until) : null;
  const changed = setting.changed ? changedLine(setting.changed) : null;
  const on = setting.email === 'on';
  const sending =
    reminders && ask?.choice === setting.email
      ? SENDING.changingReminders
      : turningEmail(ask?.choice ?? (on ? 'off' : 'on'), title);
  return (
    <div data-setting={keyOf(setting)} className="py-4">
      <h3 className="text-body font-semibold text-pretty text-text-body">{title}</h3>
      {about === null ? null : (
        <p id={aboutId} className={LINE}>
          {about}
        </p>
      )}
      {setting.email === 'not_offered' ? (
        <p className={LINE}>{SENDING.notByEmail}</p>
      ) : (
        <EmailSwitch
          title={title}
          on={on}
          pressable={setting.switch === 'offered' && !pending}
          dimmed={setting.switch !== 'offered' || pending}
          onPress={() => choose(setting, { email: on ? 'off' : 'on', ...whenOf(setting) })}
        />
      )}
      {setting.byText && texts !== 'none' ? <p className={LINE}>{SENDING.byText}</p> : null}
      {lock ? <Said {...lock} /> : null}
      {setting.officeCalls ? <p className={LINE}>{SENDING.officeCalls}</p> : null}
      {held ? <p className={MUTED}>{held}</p> : null}
      {changed ? <p className={MUTED}>{changed}</p> : null}
      {ask ? <AskSaid ask={ask} sending={sending} /> : null}
      {here !== undefined && unread ? <Said words={SENDING.unreadBeforePhone} phone /> : null}
      {kept && ask === undefined ? <Said words={SENDING.keptBeforePhone} phone /> : null}
      {problem ? <ProblemSaid problem={problem} /> : null}
      {reminders && on && setting.switch !== 'none' ? (
        <ReminderTimes setting={setting} pending={pending} choose={choose} />
      ) : null}
    </div>
  );
}

/** A reminder's days before and time of day as ops holds them, sent with the switch as one row. */
function whenOf(setting: Setting): Pick<Choice, 'leadDays' | 'sendTime'> {
  if (setting.category !== 'visit_reminders') return {};
  return {
    leadDays: setting.leadDays ?? LEAD_DAYS[0],
    sendTime: setting.sendTime ?? SEND_TIMES[0],
  };
}

/**
 * The email switch: `role="switch"`, named in full ("Email for Reminders"), its state on the
 * track and in words beside it, at least 48 tall. Held on by its lock, or while a change waits on
 * ops, it is drawn dimmed and pressing it does nothing, and it keeps its place in the tab order so
 * it is still read.
 */
function EmailSwitch({
  title,
  on,
  pressable,
  dimmed,
  onPress,
}: {
  readonly title: string;
  readonly on: boolean;
  readonly pressable: boolean;
  readonly dimmed: boolean;
  readonly onPress: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={emailFor(title)}
      aria-disabled={!pressable}
      onClick={pressable ? onPress : undefined}
      className={`mt-2 inline-flex min-h-tap items-center gap-3 rounded-control pr-2 ${
        pressable ? 'cursor-pointer' : 'cursor-not-allowed'
      } ${dimmed ? 'opacity-60' : ''}`}
    >
      <span
        aria-hidden="true"
        className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 ${
          on ? 'border-surface-brand bg-surface-brand' : 'border-border-strong bg-surface-sunken'
        }`}
      >
        <span
          className={`absolute top-0.5 size-5 rounded-full border border-border-strong bg-surface-raised ${
            on ? 'left-5.5' : 'left-0.5'
          }`}
        />
      </span>
      <span aria-hidden="true" className="text-body text-text-body">
        {SENDING.email}: {on ? SENDING.on : SENDING.off}
      </span>
    </button>
  );
}

/** The reminders' days before and time of day, and the button that sends them. */
function ReminderTimes({
  setting,
  pending,
  choose,
}: {
  readonly setting: Setting;
  readonly pending: boolean;
  readonly choose: (setting: Setting, choice: Choice) => void;
}) {
  const held = whenOf(setting);
  const [leadDays, setLeadDays] = useState(held.leadDays ?? LEAD_DAYS[0]);
  const [sendTime, setSendTime] = useState(held.sendTime ?? SEND_TIMES[0]);
  // A push that brings other days or another time draws them.
  useEffect(() => {
    setLeadDays(held.leadDays ?? LEAD_DAYS[0]);
    setSendTime(held.sendTime ?? SEND_TIMES[0]);
  }, [held.leadDays, held.sendTime]);
  const whenId = useId();
  const timeId = useId();
  const changed = leadDays !== held.leadDays || sendTime !== held.sendTime;
  const select =
    'mt-1 block min-h-tap w-full rounded-control border-2 border-border-strong bg-surface-raised px-3 text-body text-text-body';
  return (
    <div data-reminder-times className="mt-3 grid gap-3">
      <div>
        <label htmlFor={whenId} className="text-small font-semibold text-text-body">
          {SENDING.when}
        </label>
        <select
          id={whenId}
          value={leadDays}
          disabled={pending}
          onChange={(event) => setLeadDays(Number(event.target.value))}
          className={select}
        >
          {LEAD_DAYS.map((days) => (
            <option key={days} value={days}>
              {SENDING.leadDays[days]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={timeId} className="text-small font-semibold text-text-body">
          {SENDING.timeOfDay}
        </label>
        <select
          id={timeId}
          value={sendTime}
          disabled={pending}
          onChange={(event) => setSendTime(event.target.value)}
          className={select}
        >
          {SEND_TIMES.map((time) => (
            <option key={time} value={time}>
              {SENDING.sendTimes[time]}
            </option>
          ))}
        </select>
      </div>
      {changed && !pending ? (
        <button
          type="button"
          onClick={() => choose(setting, { email: 'on', leadDays, sendTime })}
          className={`cursor-pointer ${CARD_BUTTON}`}
        >
          {SENDING.changeReminders}
        </button>
      ) : null}
    </div>
  );
}

/** Her texts, as ops says they stand, and the one way to turn them off. */
function Texts({
  place,
  problem,
  here,
  keptAsTheyWere,
  unread,
  stop,
}: {
  readonly place: SendingPlace;
  readonly problem: Problem | undefined;
  /** Her texts' turning off, taken by the door from this page, is held here. */
  readonly here: boolean;
  /** Ops's push answered her turning off with her texts still on. */
  readonly keptAsTheyWere: boolean;
  readonly unread: boolean;
  readonly stop: () => void;
}) {
  const texts = place.texts!;
  const [asking, setAsking] = useState(false);
  const turnOff = useRef<HTMLButtonElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  // Taken by the door from this page, it is on its way until a read made after it shows its ask.
  const ask: SendingAsk | undefined = here ? { state: 'sending' } : place.textsAsk;
  const pending = waiting(ask);
  /** Kept: the focus goes back to "Turn off texts" once it is drawn again. */
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (asking) confirm.current?.focus();
  }, [asking]);
  useEffect(() => {
    if (!kept) return;
    turnOff.current?.focus();
    setKept(false);
  }, [kept]);
  const keep = () => {
    setAsking(false);
    setKept(true);
  };
  return (
    <section data-texts aria-labelledby={headingId} className={`${CARD} py-5`}>
      <h2 id={headingId} className={SECTION_HEADING}>
        {SENDING.texts}
      </h2>
      {texts.state === 'on' ? (
        <>
          <p className={LINE}>{SENDING.textsOn}</p>
          {place.textHours ? (
            <TextHours from={place.textHours.from} until={place.textHours.until} />
          ) : null}
          {/* Turned off here, they are off by her choice, which the website's box turns back on. */}
          <p className={LINE}>{SENDING.textsStayOffUntilTicked}</p>
          <p className={LINE}>{SENDING.replyStop}</p>
          {ask ? <AskSaid ask={ask} sending={SENDING.turningOffTexts} /> : null}
          {here && unread ? <Said words={SENDING.unreadBeforePhone} phone /> : null}
          {keptAsTheyWere && ask === undefined ? (
            <Said words={SENDING.textsKeptBeforePhone} phone />
          ) : null}
          {problem ? <ProblemSaid problem={problem} /> : null}
          {pending ? null : asking ? (
            <div className="mt-3 grid gap-2">
              <p className="text-body font-semibold text-text-body">{SENDING.turnOffEveryText}</p>
              <button
                ref={confirm}
                type="button"
                onClick={() => {
                  setAsking(false);
                  stop();
                }}
                className={`cursor-pointer ${NAVY_BUTTON}`}
              >
                {SENDING.turnOffTexts}
              </button>
              <button type="button" onClick={keep} className={`cursor-pointer ${GHOST_BUTTON}`}>
                {SENDING.keepTexts}
              </button>
            </div>
          ) : (
            <button
              ref={turnOff}
              type="button"
              onClick={() => setAsking(true)}
              className={`mt-3 cursor-pointer ${CARD_BUTTON}`}
            >
              {SENDING.turnOffTexts}
            </button>
          )}
        </>
      ) : (
        <TextsOff state={texts.state} turnOnBy={texts.turnOnBy} />
      )}
    </section>
  );
}

/**
 * The one way back ops names for her texts (11.3's `turn_on_by`, as ops #76 of 2026-10-23 gives
 * it): the website's box, her START to the business line, or, for a word home does not know or
 * none, a call. Each ends in the office's number.
 */
function WayBack({ turnOnBy }: { readonly turnOnBy: string | undefined }) {
  if (turnOnBy === 'website_box') return <Said words={SENDING.tickTheBoxBeforePhone} phone />;
  if (turnOnBy === 'keyword') return <Said words={TEXT_START} phone={false} />;
  return <Said words={SENDING.textsBackBeforePhone} phone />;
}

function TextHours({ from, until }: { readonly from: string; readonly until: string }) {
  const words = textHoursLine(from, until);
  return words === null ? null : <p className={LINE}>{words}</p>;
}

/**
 * Her texts, off or never on, each with its one way back. Off by her STOP, the way back is her
 * START, which ops names `keyword` (ops #76 of 2026-10-23) and which alone lifts a STOP, so it is
 * said whatever ops names.
 */
function TextsOff({
  state,
  turnOnBy,
}: {
  readonly state: string;
  readonly turnOnBy: string | undefined;
}) {
  const ticked = turnOnBy === 'website_box';
  switch (state) {
    case 'off_by_choice':
      return (
        <>
          <p className={LINE}>{SENDING.textsOff}</p>
          <WayBack turnOnBy={turnOnBy} />
        </>
      );
    case 'off_by_stop':
      return <Said words={TEXTS_OFF_BY_STOP} phone={false} />;
    case 'marked':
      return <Said words={SENDING.textsMarkedBeforePhone} phone />;
    case 'no_consent':
      return (
        <>
          <p className={LINE}>{SENDING.noTexts}</p>
          <Said words={ticked ? SENDING.optInBeforePhone : SENDING.askForTextsBeforePhone} phone />
        </>
      );
    case 'none':
      return (
        <>
          <p className={LINE}>{SENDING.noMobile}</p>
          <Said words={SENDING.addMobileBeforePhone} phone />
        </>
      );
    default:
      return null;
  }
}
